// 36T Design Tokens
//
// Palette taken from the Navigator app's own CSS custom properties
// (navigator.36t.solutions), which is the closest thing 36T has to a design
// system, cross-checked against the marketing site (36t.solutions).
//
//   --background #232c3d   --card #2b354a   --secondary/--border #334059
//   --foreground #f5f5f5   --muted-foreground #a3a2a2
//   --accent/--ring #f0875d   --destructive #c5503e
//
// 36T is a dark-first brand: the dark values below are the brand's real
// colours, and the light values are derived from them so the light theme
// stays recognisable rather than reverting to Swiish's stone/violet.

module.exports = {
  // Variant id. Must match the key in THEME_FILES (src/App.js) and the
  // body class the variant CSS is scoped to (.theme-36t in src/index.css).
  name: '36t',

  // Colors
  colors: {
    // Main backgrounds
    main: {
      light: '#f5f5f5',   // derived: brand foreground used as a page background
      dark: '#232c3d',    // --background
    },

    // Card backgrounds
    card: {
      light: '#ffffff',
      dark: '#2b354a',    // --card / --popover
    },

    // Secondary card/surface backgrounds
    surface: {
      light: '#e9ebef',
      dark: '#334059',    // --secondary
    },

    // Primary action button
    action: {
      light: '#f0875d',   // --accent, the one warm colour in the brand
      dark: '#f0875d',
    },

    // Primary action button hover
    actionHover: {
      light: '#d9714a',   // derived: accent darkened for light surfaces
      dark: '#f4a183',    // derived: accent lightened for dark surfaces
    },

    // Primary text
    textPrimary: {
      light: '#232c3d',
      dark: '#f5f5f5',    // --foreground
    },

    // Secondary text
    textSecondary: {
      light: '#334059',
      dark: '#dfe3ea',
    },

    // Muted text
    textMuted: {
      light: '#5f6a7d',
      dark: '#a3a2a2',    // --muted-foreground
    },

    // Very muted text
    textMutedSubtle: {
      light: '#8b93a1',
      dark: '#7b8496',
    },

    // Borders
    border: {
      light: '#c9cfd9',
      dark: '#334059',    // --border / --input
    },

    // Borders (subtle)
    borderSubtle: {
      light: '#dfe3e9',
      dark: '#2b354a',
    },

    // Success - not defined by the brand; a desaturated green chosen to sit
    // quietly next to the coral accent instead of competing with it.
    success: {
      light: '#2f8f6b',
      dark: '#3fa87e',
    },

    successHover: {
      light: '#276f54',
      dark: '#52bd92',
    },

    successBg: {
      light: '#e6f4ee',
      dark: 'rgba(63, 168, 126, 0.15)',
    },

    successText: {
      light: '#2f8f6b',
      dark: '#6fc7a5',
    },

    successBorder: {
      light: '#bfe3d4',
      dark: 'rgba(63, 168, 126, 0.4)',
    },

    // Error/Destructive
    error: {
      light: '#c5503e',   // --destructive
      dark: '#d4604e',
    },

    errorHover: {
      light: '#a53f30',
      dark: '#e07766',
    },

    errorBg: {
      light: '#fbeae7',
      dark: 'rgba(197, 80, 62, 0.18)',
    },

    errorText: {
      light: '#c5503e',
      dark: '#e58b7c',
    },

    errorBorder: {
      light: '#f0c6bf',
      dark: 'rgba(197, 80, 62, 0.45)',
    },

    // Info - built from the brand's own navy rather than a generic blue.
    info: {
      light: '#334059',   // --secondary
      dark: '#43536f',
    },

    infoHover: {
      light: '#283349',
      dark: '#54668a',
    },

    infoBg: {
      light: '#eceef2',
      dark: 'rgba(51, 64, 89, 0.5)',
    },

    infoText: {
      light: '#334059',
      dark: '#b6c0d4',
    },

    infoBorder: {
      light: '#ccd2dd',
      dark: '#43536f',
    },

    // Link colors - the accent doubles as the link colour, as on the site.
    link: {
      light: '#c9643f',
      dark: '#f0875d',
    },

    linkBg: {
      light: '#fdf0ea',
      dark: 'rgba(240, 135, 93, 0.15)',
    },

    linkBorder: {
      light: '#f6d5c6',
      dark: 'rgba(240, 135, 93, 0.35)',
    },

    linkHover: {
      light: '#f9e2d7',
      dark: 'rgba(240, 135, 93, 0.25)',
    },

    // Input fields
    inputBg: {
      light: '#ffffff',
      dark: '#2b354a',
    },

    inputBorder: {
      light: '#c9cfd9',
      dark: '#334059',    // --input
    },

    inputText: {
      light: '#232c3d',
      dark: '#f5f5f5',
    },

    // Focus ring
    focusRing: {
      light: '#f0875d',   // --ring
      dark: '#f0875d',
    },

    // Modal overlay
    overlay: {
      light: 'rgba(35, 44, 61, 0.6)',
      dark: 'rgba(16, 20, 28, 0.8)',
    },

    // Confirm/Neutral button
    confirm: {
      light: '#c9cfd9',
      dark: '#43536f',
    },

    confirmHover: {
      light: '#b3bbc9',
      dark: '#54668a',
    },

    confirmText: {
      light: '#232c3d',
      dark: '#f5f5f5',
    },

    confirmBg: {
      light: '#eceef2',
      dark: '#2b354a',
    },

    confirmBorder: {
      light: '#dfe3e9',
      dark: '#334059',
    },
  },

  // Border Radius
  // 36T draws with near-square corners: the site uses 4px and the Navigator
  // 4-6px. Swiish ships 40px cards and pill buttons, which is the single
  // biggest reason its stock look reads as a different brand.
  borderRadius: {
    'card': '8px',
    'container': '6px',
    'page': '8px',
    'button': '6px',
    'input': '6px',
    'badge': '4px',
    'full': '9999px',   // kept: avatars and icon tiles are still circles
  },

  // Border Width
  borderWidth: {
    'default': '1px',
    'thick': '2px',
    'avatar': '4px',
    'device': '8px',
  },

  // Background Textures
  // Disabled. 36T's surfaces are flat; Swiish's sand texture is its own brand.
  textures: {
    main: {
      light: '',
      dark: '',
      size: '1080px 1080px',
      blendLight: 'multiply',
      blendDark: 'overlay',
      opacityLight: 0,
      opacityDark: 0,
    },
    surface: {
      light: '',
      dark: '',
    },
    card: {
      light: '',
      dark: '',
    },
  },
};
